/*
 * INVOICE LIST PAGE
 * =================
 * This screen shows invoice records that already came through the API mapping
 * layer. In other words, the page should think in "frontend invoice fields",
 * not in raw SQL column names.
 *
 * The main fields expected here are:
 * - `InvoiceId`
 * - `InvoiceNumber`
 * - `Client`
 * - `Description`
 * - `TotalAmount`
 * - `IssueDate`
 * - `DueDate`
 * - `Status`
 *
 * If the database schema changes, the preferred place to adapt the shape is
 * `api/invoices/index.ts`, so this screen can stay simple.
 */

console.log("[InvoiceList] AuthContext import url:", import.meta.url);
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz, type ColDef, type GridApi, type ICellRendererParams, type ValueFormatterParams } from 'ag-grid-community';
import { Bell, Calendar, CheckCircle2, ChevronLeft, ChevronRight, Mail, RefreshCw } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
// Dynamic import to avoid timing issues with AuthContext initialization
import { useAuth } from '@/components/Auth/AuthContext';
import PageHeader from '@/components/Layout/PageHeader';
import { daysSince } from '@/utils/ageBadge';
import { getCompanyAiFocusDomId, matchesCompanyAiFocusRecord, readCompanyAiFocusTarget, readCompanyAiFollowUpSeed, readCompanyAiPeriodFilter } from '@/utils/companyAiNavigation';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { hasDateRange, normalizeDateOnlyInput } from '@/utils/listDateFilters';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getPrimaryRoleLabel, hasRequiredRoleAccess } from '@/utils/swaRole';
import { Button, Card, DataRow, EmptyState, Modal, PageSection, Select, StatusBadge } from '@/ui/components';
import ListPaginationControls from '@/components/List/ListPaginationControls';
import EmailSendModal, { type EmailModalTarget } from '@/components/Email/EmailSendModal';
import { DEFAULT_INVOICE_STATUS_FILTER, getEffectiveInvoiceStatus, getInvoicePaymentState, matchesStatusFilter, type InvoiceStatusFilter } from './invoiceStatus';
import '@/pages/dataGridFocus.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const WILSON_EMAIL = 'wd@linfinityeng.com';

type InvoiceSortBy = 'invoiceNumber' | 'client' | 'amount' | 'status' | 'date';
type SortDirection = 'asc' | 'desc';
type InvoiceViewMode = 'cards' | 'table';
const LIST_PAGE_SIZE = 20;
const LIST_PAGE_SIZE_OPTIONS = [20, 50, 100];
const ANALYTICS_PAGE_SIZE = 500;
const invoiceGridTheme = themeQuartz;

function getDefaultSortDirection(sortBy: InvoiceSortBy): SortDirection {
  return 'desc';
}

function isHttpUrl(value?: string | null) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

function getInvoiceRowId(invoice: any) {
  return String(invoice?.InvoiceId ?? invoice?.Id ?? invoice?.InvoiceNumber ?? invoice?.InvoiceNo ?? '').trim();
}

function setInvoiceRowsPaymentState(rows: any[], invoiceIds: Set<string>, paid: boolean, paidDate: string) {
  if (!invoiceIds.size) return rows;

  return rows.map((invoice) => {
    const invoiceId = getInvoiceRowId(invoice);
    if (!invoiceId || !invoiceIds.has(invoiceId)) return invoice;

    return {
      ...invoice,
      Status: paid ? 'Paid' : 'Sent',
      PaymentStatus: paid ? 'Paid' : 'Unpaid',
      PaidYN: paid ? 'Yes' : 'No',
      Paid: paid ? 'Yes' : 'No',
      PaidDate: paid ? (invoice.PaidDate || paidDate) : null,
      PaidOn: paid ? (invoice.PaidOn || paidDate) : null,
    };
  });
}

function isReminderEligibleInvoice(invoice: any) {
  const normalizedStatus = getEffectiveInvoiceStatus(invoice).trim().toLowerCase();
  return normalizedStatus === 'sent' || normalizedStatus === 'unpaid' || normalizedStatus === 'overdue';
}

function isEmailNotificationEligibleInvoice(invoice: any) {
  if (getInvoicePaymentState(invoice) === 'Paid') return false;
  const status = getEffectiveInvoiceStatus(invoice).trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (['draft', 'filedpending', 'filedpendingsend', 'superseded', 'cancelled', 'canceled'].includes(status)) return false;
  const issued = Boolean(invoice?.SentDate || invoice?.IssueDate)
    || ['issued', 'sent', 'unpaid', 'overdue'].includes(status);
  if (!issued) return false;
  const dueDate = new Date(String(invoice?.DueDate || '').trim());
  if (Number.isNaN(dueDate.getTime())) return false;
  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate.getTime() <= today.getTime();
}

function getInvoiceOverdueDays(invoice: any) {
  if (getInvoicePaymentState(invoice) === 'Paid') return null;

  const dueDate = new Date(String(invoice?.DueDate || '').trim());
  if (Number.isNaN(dueDate.getTime())) return null;

  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : null;
}

function getInvoiceDueTiming(invoice: any) {
  if (getInvoicePaymentState(invoice) === 'Paid') {
    return { label: 'Paid', tone: 'paid' };
  }

  const dueDate = new Date(String(invoice?.DueDate || '').trim());
  if (Number.isNaN(dueDate.getTime())) {
    return { label: 'Due date pending', tone: 'neutral' };
  }

  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const overdueDays = Math.abs(diffDays);
    return { label: `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`, tone: 'overdue' };
  }

  if (diffDays === 0) {
    return { label: 'Due today', tone: 'due' };
  }

  return { label: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`, tone: 'due' };
}

function getInvoiceAmount(invoice: any) {
  const amount = typeof invoice?.TotalAmount === 'number'
    ? invoice.TotalAmount
    : Number(String(invoice?.TotalAmount ?? '').trim());
  return Number.isFinite(amount) ? amount : 0;
}

function normalizeLocationPart(value: unknown) {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

function appendUniqueLocationPart(parts: string[], value: unknown) {
  const normalized = normalizeLocationPart(value);
  if (!normalized) return;

  const comparable = normalized.toLowerCase();
  if (parts.some((part) => part.toLowerCase() === comparable || part.toLowerCase().includes(comparable))) {
    return;
  }
  parts.push(normalized);
}

function buildInvoiceProjectLocation(invoice: any) {
  const parts: string[] = [];
  appendUniqueLocationPart(parts, invoice?.Description || invoice?.ProjectAddress || invoice?.ProjectLocation || invoice?.SiteAddress);
  appendUniqueLocationPart(parts, invoice?.Suburb || invoice?.ProjectSuburb || invoice?.SiteSuburb);
  appendUniqueLocationPart(parts, invoice?.Postcode || invoice?.ProjectPostcode || invoice?.PostalCode || invoice?.Zip);
  return parts.join(', ');
}

function compareDateValues(a: any, b: any) {
  const first = new Date(a || 0).getTime();
  const second = new Date(b || 0).getTime();
  const safeFirst = Number.isNaN(first) ? 0 : first;
  const safeSecond = Number.isNaN(second) ? 0 : second;
  return safeFirst - safeSecond;
}

function compareDateForGridFilter(filterDate: Date, cellValue: any) {
  const parsed = new Date(cellValue || '');
  if (Number.isNaN(parsed.getTime())) return -1;
  parsed.setHours(0, 0, 0, 0);
  const filterTime = filterDate.getTime();
  const cellTime = parsed.getTime();
  if (cellTime < filterTime) return -1;
  if (cellTime > filterTime) return 1;
  return 0;
}

function getReminderBlockedReason(invoice: any) {
  if (!isReminderEligibleInvoice(invoice)) {
    return `${getEffectiveInvoiceStatus(invoice)} invoices are not reminder-eligible`;
  }

  const overdueDays = getInvoiceOverdueDays(invoice);
  if (overdueDays === null || overdueDays <= 14) {
    return 'Invoice must be more than 14 days overdue before sending a reminder.';
  }

  return null;
}

function canPreviewReminderInvoice(invoice: any) {
  return getReminderBlockedReason(invoice) === null;
}

type ReminderPreviewData = {
  source?: string;
  invoiceId?: string | number;
  invoiceNumber?: string;
  senderEmail?: string | null;
  recipientEmail?: string | null;
  ccEmails?: string[] | null;
  subject?: string | null;
  html?: string | null;
  status?: string | null;
  canSend?: boolean;
  validationMessage?: string | null;
};

function normalizeListQueryInput(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeComparableText(value?: string | null) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

type CalendarMonth = {
  year: number;
  monthIndex: number;
};

const calendarWeekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseDateKey(value?: string | null) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12) return null;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;

  return { year, monthIndex: month - 1, day };
}

function toDateKey(year: number, monthIndex: number, day: number) {
  return [
    String(year).padStart(4, '0'),
    String(monthIndex + 1).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
}

function monthFromDateKey(value?: string | null): CalendarMonth {
  const parsed = parseDateKey(value);
  if (parsed) {
    return { year: parsed.year, monthIndex: parsed.monthIndex };
  }

  const today = new Date();
  return { year: today.getFullYear(), monthIndex: today.getMonth() };
}

function addCalendarMonths(month: CalendarMonth, offset: number): CalendarMonth {
  const next = new Date(month.year, month.monthIndex + offset, 1);
  return { year: next.getFullYear(), monthIndex: next.getMonth() };
}

function formatDateFilterLabel(value: string) {
  const parsed = parseDateKey(value);
  if (!parsed) return 'Any date';
  return `${String(parsed.day).padStart(2, '0')}/${String(parsed.monthIndex + 1).padStart(2, '0')}/${parsed.year}`;
}

type InvoiceDateFilterPickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

export function InvoiceDateFilterPicker({ label, value, onChange }: InvoiceDateFilterPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState<CalendarMonth>(() => monthFromDateKey(value));
  const selectedDate = parseDateKey(value);

  useEffect(() => {
    if (!isOpen) {
      setVisibleMonth(monthFromDateKey(value));
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isOpen]);

  const monthLabel = useMemo(() => {
    return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' })
      .format(new Date(visibleMonth.year, visibleMonth.monthIndex, 1));
  }, [visibleMonth.monthIndex, visibleMonth.year]);

  const calendarDays = useMemo(() => {
    const firstDayOffset = new Date(visibleMonth.year, visibleMonth.monthIndex, 1).getDay();
    const daysInMonth = new Date(visibleMonth.year, visibleMonth.monthIndex + 1, 0).getDate();
    return [
      ...Array.from({ length: firstDayOffset }, () => null),
      ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
    ];
  }, [visibleMonth.monthIndex, visibleMonth.year]);

  const openPicker = () => {
    setVisibleMonth(monthFromDateKey(value));
    setIsOpen((current) => !current);
  };

  const selectDay = (day: number) => {
    onChange(toDateKey(visibleMonth.year, visibleMonth.monthIndex, day));
    setIsOpen(false);
  };

  return (
    <div className="invoice-date-picker" ref={containerRef}>
      <button
        type="button"
        className="invoice-date-picker__trigger"
        onClick={openPicker}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>{formatDateFilterLabel(value)}</span>
        <Calendar size={18} aria-hidden="true" />
      </button>

      {isOpen ? (
        <div className="invoice-date-picker__popover" role="dialog" aria-label={`${label} calendar`}>
          <div className="invoice-date-picker__header">
            <button
              type="button"
              className="invoice-date-picker__nav"
              onClick={() => setVisibleMonth((current) => addCalendarMonths(current, -1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <div className="invoice-date-picker__month" aria-live="polite">{monthLabel}</div>
            <button
              type="button"
              className="invoice-date-picker__nav"
              onClick={() => setVisibleMonth((current) => addCalendarMonths(current, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>

          <div className="invoice-date-picker__weekdays" aria-hidden="true">
            {calendarWeekdayLabels.map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>

          <div className="invoice-date-picker__grid">
            {calendarDays.map((day, index) => {
              if (!day) {
                return <span key={`blank-${index}`} aria-hidden="true" />;
              }

              const isSelected = selectedDate?.year === visibleMonth.year
                && selectedDate.monthIndex === visibleMonth.monthIndex
                && selectedDate.day === day;

              return (
                <button
                  key={day}
                  type="button"
                  className={`invoice-date-picker__day${isSelected ? ' invoice-date-picker__day--selected' : ''}`}
                  onClick={() => selectDay(day)}
                  aria-pressed={isSelected}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [analyticsInvoices, setAnalyticsInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<InvoiceSortBy>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('date'));
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>(DEFAULT_INVOICE_STATUS_FILTER);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, pageSize: LIST_PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [viewMode, setViewMode] = useState<InvoiceViewMode>('cards');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<null | 'export' | 'pdf' | 'reminder' | 'paid'>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderPreviewBusy, setReminderPreviewBusy] = useState(false);
  const [reminderPreviewError, setReminderPreviewError] = useState<string | null>(null);
  const [reminderPreviewData, setReminderPreviewData] = useState<ReminderPreviewData | null>(null);
  const [reminderPreviewInvoice, setReminderPreviewInvoice] = useState<any | null>(null);
  const [emailTarget, setEmailTarget] = useState<EmailModalTarget | null>(null);
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi<any> | null>(null);
  const isNarrowScreen = useIsNarrowScreen(640);
  const currentUserEmail = (user?.userDetails || '').trim().toLowerCase();
  const isCivilInvoiceUser = currentUserEmail === WILSON_EMAIL;
  const pageTitle = isCivilInvoiceUser ? 'Civil Invoices' : 'Invoices';
  const pageSubtitle = isCivilInvoiceUser
    ? 'Civil invoices are loaded for Wilson by default.'
    : 'Current invoices are loaded for your account by default.';
  const aiFocus = readCompanyAiFocusTarget(location.state, 'invoices');
  const aiPeriodFilter = readCompanyAiPeriodFilter(location.state, 'invoices');
  const aiFollowUp = readCompanyAiFollowUpSeed(location.state);
  const hasActiveDateRange = hasDateRange(dateFrom, dateTo);
  const activeDateBasis = aiPeriodFilter?.dateBasis || 'issue date';
  const isUsingAiPeriodRange = Boolean(
    aiPeriodFilter
    && normalizeDateOnlyInput(aiPeriodFilter.startDate) === dateFrom
    && normalizeDateOnlyInput(aiPeriodFilter.endDate) === dateTo
  );

  const formatMoney = (amount: any) => {
    const n = typeof amount === 'number' ? amount : Number(String(amount ?? '').trim());
    const value = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDateLabel = (value: any) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-AU');
  };

  const escapeCsvValue = (value: any) => {
    const normalized = value == null ? '' : String(value);
    if (!/[",\r\n]/.test(normalized)) return normalized;
    return `"${normalized.replace(/"/g, '""')}"`;
  };

  useEffect(() => {
    // Wait for auth to be ready
    if (!isAuthenticated || !user) return;
    const timer = window.setTimeout(() => {
      void loadInvoices(searchQuery.trim());
    }, searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated, user, searchQuery, dateFrom, dateTo, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, dateFrom, dateTo, activeDateBasis, statusFilter]);

  useEffect(() => {
    setAnalyticsInvoices([]);
  }, [searchQuery, dateFrom, dateTo, activeDateBasis]);

  useEffect(() => {
    if (!aiFocus && !aiPeriodFilter) {
      return;
    }

    setStatusFilter('all');
    if (aiPeriodFilter) {
      setSearchQuery('');
      setDateFrom(normalizeDateOnlyInput(aiPeriodFilter.startDate));
      setDateTo(normalizeDateOnlyInput(aiPeriodFilter.endDate));
      return;
    }

    setSearchQuery((current) => current.trim() || aiFocus.searchText);
  }, [aiFocus?.recordId, aiFocus?.searchText, aiPeriodFilter?.startDate, aiPeriodFilter?.endDate, aiPeriodFilter?.dateBasis]);

  const loadInvoices = async (rawQuery: unknown = searchQuery.trim(), forceRefresh = false) => {
    console.log('🧾 Starting invoice load...');
    console.log('🧾 User authenticated:', !!user);
    const query = normalizeListQueryInput(rawQuery);

    try {
      if (forceRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      // Dynamic import to ensure AuthContext is initialized first
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      if (forceRefresh) azureSqlService.clearCache('invoices');

      // Load invoice data from Azure SQL Database (Invoices table)
      console.log('🧾 Loading invoice data from Azure SQL...');
      const queryPayload = {
        ...(query ? { q: query } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(hasActiveDateRange ? { dateBasis: activeDateBasis } : {}),
      };
      const invoiceResult = await azureSqlService.getInvoices({
        page,
        pageSize,
        query: queryPayload,
        useCache: !forceRefresh && !query && !hasActiveDateRange && page === 1,
        returnEnvelope: true,
      });
      const data = invoiceResult?.data || [];
      const totalRecords = Number(invoiceResult?.pagination?.totalRecords || data.length || 0);
      if (totalRecords > data.length) {
        const analyticsResult = await azureSqlService.getInvoices({
          page: 1,
          pageSize: Math.min(Math.max(totalRecords, data.length), ANALYTICS_PAGE_SIZE),
          query: queryPayload,
          useCache: false,
          returnEnvelope: true,
        });
        setAnalyticsInvoices(analyticsResult?.data || data || []);
      } else {
        setAnalyticsInvoices(data || []);
      }

      console.log('🧾 Invoice data received:', data);
      console.log('🧾 Invoice count:', data?.length);

      setInvoices(data || []);
      setPagination({
        page: Number(invoiceResult?.pagination?.page || page),
        pageSize: Number(invoiceResult?.pagination?.pageSize || pageSize),
        totalRecords,
        totalPages: Math.max(Number(invoiceResult?.pagination?.totalPages || 1), 1),
      });
      console.log('🧾 Invoices set successfully');

    } catch (err) {
      console.error('🧾 Error loading invoices:', err);
      setError(`Failed to load invoices: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
      console.log('🧾 Loading complete');
    }
  };

  // Filter invoices based on search query + status
  const filteredInvoices = invoices
    .filter((invoice) => matchesStatusFilter(invoice, statusFilter))
    .filter((invoice) => {
      return true;
    });

  // Sort invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'invoiceNumber':
        comparison = (a.InvoiceNumber || '').localeCompare(b.InvoiceNumber || '');
        break;
      case 'client':
        comparison = (a.Client || '').localeCompare(b.Client || '');
        break;
      case 'amount':
        comparison = getInvoiceAmount(a) - getInvoiceAmount(b);
        break;
      case 'status':
        comparison = getEffectiveInvoiceStatus(a).localeCompare(getEffectiveInvoiceStatus(b));
        break;
      case 'date':
        comparison = compareDateValues(a.IssueDate, b.IssueDate);
        break;
      default:
        comparison = 0;
    }

    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });

  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const canManageInvoiceMutations = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['finance']);
  const visibleInvoices = sortedInvoices;
  const analyticsSourceInvoices = analyticsInvoices.length > 0 ? analyticsInvoices : invoices;
  const analyticsVisibleInvoices = [...analyticsSourceInvoices]
    .filter((invoice) => matchesStatusFilter(invoice, statusFilter))
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'invoiceNumber':
          comparison = (a.InvoiceNumber || '').localeCompare(b.InvoiceNumber || '');
          break;
        case 'client':
          comparison = (a.Client || '').localeCompare(b.Client || '');
          break;
        case 'amount':
          comparison = getInvoiceAmount(a) - getInvoiceAmount(b);
          break;
        case 'status':
          comparison = getEffectiveInvoiceStatus(a).localeCompare(getEffectiveInvoiceStatus(b));
          break;
        case 'date':
          comparison = compareDateValues(a.IssueDate, b.IssueDate);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'desc' ? comparison * -1 : comparison;
    });
  const analyticsScopeLabel = analyticsInvoices.length > visibleInvoices.length
    ? `${analyticsVisibleInvoices.length} analysed`
    : `${visibleInvoices.length} visible`;
  const selectedInvoiceRecords = sortedInvoices.filter((invoice) => {
    const invoiceId = getInvoiceRowId(invoice);
    return invoiceId ? selectedInvoices.has(invoiceId) : false;
  });
  const selectedReminderCandidates = selectedInvoiceRecords.filter(canPreviewReminderInvoice);
  const selectedPaymentCandidates = selectedInvoiceRecords.filter((invoice) => getInvoicePaymentState(invoice) !== 'Paid');
  const selectedPaidCandidates = selectedInvoiceRecords.filter((invoice) => getInvoicePaymentState(invoice) === 'Paid');
  const invoiceSummary = useMemo(() => {
    const summary = {
      count: analyticsVisibleInvoices.length,
      totalAmount: 0,
      receivablesAmount: 0,
      overdueAmount: 0,
      paidAmount: 0,
      draftCount: 0,
      sentCount: 0,
      paidCount: 0,
      overdueCount: 0,
      thirtyPlusCount: 0,
    };

    for (const invoice of analyticsVisibleInvoices) {
      const amount = getInvoiceAmount(invoice);
      const status = getEffectiveInvoiceStatus(invoice).trim().toLowerCase();
      const paymentState = getInvoicePaymentState(invoice);
      const openedAt = invoice.Created || invoice.IssueDate || invoice.CreatedDate;
      const ageDays = paymentState === 'Paid' ? null : daysSince(openedAt);
      const overdueDays = getInvoiceOverdueDays(invoice);

      summary.totalAmount += amount;
      if (paymentState === 'Paid') {
        summary.paidAmount += amount;
        summary.paidCount += 1;
      } else {
        summary.receivablesAmount += amount;
      }
      if (status === 'draft') summary.draftCount += 1;
      if (status === 'sent' || status === 'unpaid') summary.sentCount += 1;
      if (overdueDays !== null) {
        summary.overdueAmount += amount;
        summary.overdueCount += 1;
      }
      if (ageDays !== null && ageDays >= 30) summary.thirtyPlusCount += 1;
    }

    return summary;
  }, [analyticsVisibleInvoices]);

  const invoiceChartData = useMemo(() => {
    const monthAmounts = new Map<string, { label: string; amount: number; count: number }>();
    const clientAmounts = new Map<string, number>();
    const ageBuckets = [
      { label: '0-7 days', count: 0, amount: 0 },
      { label: '8-14 days', count: 0, amount: 0 },
      { label: '15-30 days', count: 0, amount: 0 },
      { label: '30+ days', count: 0, amount: 0 },
    ];
    const statusCounts = {
      paid: 0,
      receivable: 0,
      overdue: 0,
      draft: 0,
    };

    for (const invoice of analyticsVisibleInvoices) {
      const amount = getInvoiceAmount(invoice);
      const issueDate = new Date(invoice.IssueDate || invoice.Created || invoice.CreatedDate || '');
      const paymentState = getInvoicePaymentState(invoice);
      const effectiveStatus = getEffectiveInvoiceStatus(invoice).trim().toLowerCase();
      const openedAt = invoice.Created || invoice.IssueDate || invoice.CreatedDate;
      const ageDays = paymentState === 'Paid' ? null : daysSince(openedAt);
      const overdueDays = getInvoiceOverdueDays(invoice);
      const clientLabel = String(invoice.Client || invoice.ClientName || 'Client pending').trim() || 'Client pending';

      if (!Number.isNaN(issueDate.getTime())) {
        const key = `${issueDate.getFullYear()}-${String(issueDate.getMonth() + 1).padStart(2, '0')}`;
        const existing = monthAmounts.get(key) || {
          label: issueDate.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
          amount: 0,
          count: 0,
        };
        existing.amount += amount;
        existing.count += 1;
        monthAmounts.set(key, existing);
      }

      if (paymentState === 'Paid') {
        statusCounts.paid += 1;
      } else if (effectiveStatus === 'draft') {
        statusCounts.draft += 1;
      } else if (overdueDays !== null) {
        statusCounts.overdue += 1;
      } else {
        statusCounts.receivable += 1;
      }

      if (paymentState !== 'Paid') {
        clientAmounts.set(clientLabel, (clientAmounts.get(clientLabel) || 0) + amount);
      }

      if (ageDays !== null) {
        const bucket = ageDays <= 7
          ? ageBuckets[0]
          : ageDays <= 14
            ? ageBuckets[1]
            : ageDays <= 30
              ? ageBuckets[2]
              : ageBuckets[3];
        bucket.count += 1;
        bucket.amount += amount;
      }
    }

    const monthly = [...monthAmounts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([, value]) => value);
    const maxMonthlyAmount = Math.max(...monthly.map((item) => item.amount), 1);
    const maxAgeCount = Math.max(...ageBuckets.map((item) => item.count), 1);
    const topClients = [...clientAmounts.entries()]
      .map(([label, amount]) => ({ label, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
    const maxClientAmount = Math.max(...topClients.map((item) => item.amount), 1);
    const statusTotal = Math.max(
      statusCounts.paid + statusCounts.receivable + statusCounts.overdue + statusCounts.draft,
      1
    );
    const paidDeg = (statusCounts.paid / statusTotal) * 360;
    const receivableDeg = paidDeg + (statusCounts.receivable / statusTotal) * 360;
    const overdueDeg = receivableDeg + (statusCounts.overdue / statusTotal) * 360;
    const statusDonut = `conic-gradient(#0b6a4c 0deg ${paidDeg}deg, #234ea8 ${paidDeg}deg ${receivableDeg}deg, #c2410c ${receivableDeg}deg ${overdueDeg}deg, #64748b ${overdueDeg}deg 360deg)`;

    return {
      monthly,
      maxMonthlyAmount,
      ageBuckets,
      maxAgeCount,
      topClients,
      maxClientAmount,
      statusCounts,
      statusDonut,
    };
  }, [analyticsVisibleInvoices]);

  const openInvoice = useCallback((invoice: any) => {
    const invoiceId = getInvoiceRowId(invoice);
    if (invoiceId) {
      navigate(`/invoices/${invoiceId}`);
    }
  }, [navigate]);

  const invoiceGridColumnDefs = useMemo<ColDef<any>[]>(() => [
    {
      headerName: 'Invoice #',
      field: 'InvoiceNumber',
      minWidth: 132,
      pinned: isNarrowScreen ? undefined : 'left',
      cellRenderer: (params: ICellRendererParams<any>) => {
        const label = String(params.value || params.data?.InvoiceNo || getInvoiceRowId(params.data) || '').trim() || 'Open';
        return (
          <button
            type="button"
            className="invoice-grid__link-button"
            onClick={() => openInvoice(params.data)}
          >
            {label}
          </button>
        );
      },
    },
    {
      headerName: 'Client',
      field: 'Client',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      valueFormatter: (params: ValueFormatterParams<any>) => String(params.value || params.data?.ClientName || 'Client pending'),
    },
    {
      headerName: 'Project',
      field: 'Description',
      minWidth: 260,
      flex: 1,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => String(params.data?.Description || params.data?.ProjectAddress || '').trim(),
      valueFormatter: (params: ValueFormatterParams<any>) => String(params.value || 'Project address pending'),
    },
    {
      headerName: 'Issue Date',
      field: 'IssueDate',
      minWidth: 130,
      filter: 'agDateColumnFilter',
      comparator: compareDateValues,
      filterParams: {
        comparator: compareDateForGridFilter,
      },
      valueFormatter: (params: ValueFormatterParams<any>) => formatDateLabel(params.value) || 'Unknown',
    },
    {
      headerName: 'Due Date',
      field: 'DueDate',
      minWidth: 130,
      filter: 'agDateColumnFilter',
      comparator: compareDateValues,
      filterParams: {
        comparator: compareDateForGridFilter,
      },
      valueFormatter: (params: ValueFormatterParams<any>) => formatDateLabel(params.value) || 'Unknown',
    },
    {
      headerName: 'Payment',
      minWidth: 124,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => getInvoicePaymentState(params.data),
      cellClass: (params) => getInvoicePaymentState(params.data) === 'Paid' ? 'invoice-grid__payment-paid' : 'invoice-grid__payment-unpaid',
    },
    {
      headerName: 'Workflow',
      minWidth: 132,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => getEffectiveInvoiceStatus(params.data),
    },
    {
      headerName: 'Amount',
      minWidth: 126,
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      valueGetter: (params) => getInvoiceAmount(params.data),
      valueFormatter: (params: ValueFormatterParams<any>) => formatMoney(params.value),
      cellClass: 'invoice-grid__amount',
    },
    {
      headerName: 'Revision',
      minWidth: 104,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) => Number(params.data?.CurrentRevisionNo || 1),
      valueFormatter: (params: ValueFormatterParams<any>) => `R${Number(params.value || 1)}`,
    },
    {
      headerName: 'Age',
      minWidth: 100,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) => {
        if (getInvoicePaymentState(params.data) === 'Paid') return null;
        return daysSince(params.data?.Created || params.data?.IssueDate || params.data?.CreatedDate);
      },
      valueFormatter: (params: ValueFormatterParams<any>) => params.value == null ? '' : `${params.value} days`,
    },
    {
      headerName: 'Overdue',
      minWidth: 112,
      filter: 'agNumberColumnFilter',
      valueGetter: (params) => getInvoiceOverdueDays(params.data),
      valueFormatter: (params: ValueFormatterParams<any>) => params.value == null ? '' : `${params.value} days`,
    },
    {
      headerName: 'SharePoint',
      minWidth: 144,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => String(params.data?.PdfFileUrl || '').trim(),
      cellRenderer: (params: ICellRendererParams<any>) => {
        const url = String(params.value || '').trim();
        if (!url) return <span className="dgf-meta-muted">Not linked</span>;
        if (!isHttpUrl(url)) return <span>Linked</span>;
        return (
          <a href={url} target="_blank" rel="noreferrer" className="dgf-link">
            Open file
          </a>
        );
      },
    },
  ], [formatMoney, formatDateLabel, isNarrowScreen, openInvoice]);

  useEffect(() => {
    if (!aiFocus || loading) {
      return;
    }

    const focusId = getCompanyAiFocusDomId('invoices', aiFocus.recordId);
    if (!focusId || !visibleInvoices.some((invoice) => matchesCompanyAiFocusRecord(aiFocus, getInvoiceRowId(invoice)))) {
      return;
    }

    const timer = window.setTimeout(() => {
      const element = document.getElementById(focusId);
      if (!element) {
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof (element as HTMLElement).focus === 'function') {
        (element as HTMLElement).focus({ preventScroll: true });
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [aiFocus, loading, visibleInvoices]);

  const clearActionFeedback = () => {
    setActionError(null);
    setActionNotice(null);
  };

  const handleSortChange = (value: string) => {
    const nextSortBy = value as InvoiceSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  const toggleSelectMode = () => {
    clearActionFeedback();
    setSelectMode((v) => {
      const next = !v;
      if (!next) setSelectedInvoices(new Set());
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedInvoices(new Set());
      return;
    }

    const all = new Set<string>();
    for (const inv of visibleInvoices) {
      const id = getInvoiceRowId(inv);
      if (id) all.add(id);
    }
    setSelectedInvoices(all);
  };

  const toggleSelection = (invoiceId: string) => {
    const next = new Set(selectedInvoices);
    if (next.has(invoiceId)) next.delete(invoiceId);
    else next.add(invoiceId);
    setSelectedInvoices(next);
  };

  const handleExportGridCsv = () => {
    clearActionFeedback();
    if (!gridApiRef.current) {
      setActionError('The invoice table is still getting ready.');
      return;
    }

    gridApiRef.current.exportDataAsCsv({
      fileName: `invoice-table-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    setActionNotice(`Exported ${visibleInvoices.length} visible invoice${visibleInvoices.length === 1 ? '' : 's'} from the table.`);
  };

  const handleExportSelected = async (targets: any[]) => {
    if (!targets.length || actionBusy) return;

    clearActionFeedback();
    setActionBusy('export');
    try {
      const headers = [
        'Invoice ID',
        'Invoice Number',
        'Status',
        'Client',
        'Client Email',
        'Project',
        'Issue Date',
        'Due Date',
        'Amount',
        'Sender Email',
        'Entity',
        'SharePoint PDF',
      ];
      const rows = targets.map((invoice) => {
        const projectLabel = String(invoice.Description || invoice.ProjectAddress || '').trim();
        return [
          getInvoiceRowId(invoice),
          invoice.InvoiceNumber || invoice.InvoiceNo || '',
          getEffectiveInvoiceStatus(invoice),
          invoice.Client || invoice.ClientName || '',
          invoice.ClientEmail || '',
          projectLabel,
          formatDateLabel(invoice.IssueDate),
          formatDateLabel(invoice.DueDate),
          Number(invoice.TotalAmount || 0).toFixed(2),
          invoice.SenderEmail || invoice.CreatedBy || '',
          invoice.Entity || '',
          invoice.PdfFileUrl || '',
        ].map(escapeCsvValue).join(',');
      });
      const csv = [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `invoice-selection-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setActionNotice(`Exported ${targets.length} selected invoice${targets.length === 1 ? '' : 's'} to CSV.`);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to export the selected invoices.');
    } finally {
      setActionBusy(null);
    }
  };

  const handleOpenSelectedPdfs = async (targets: any[]) => {
    if (!targets.length || actionBusy) return;

    clearActionFeedback();
    setActionBusy('pdf');
    try {
      const pdfTargets = targets.filter((invoice) => isHttpUrl(invoice.PdfFileUrl));
      const missingCount = targets.length - pdfTargets.length;

      if (!pdfTargets.length) {
        setActionError('None of the selected invoices have a saved PDF link yet.');
        return;
      }

      for (const invoice of pdfTargets) {
        const link = document.createElement('a');
        link.href = String(invoice.PdfFileUrl).trim();
        link.target = '_blank';
        link.rel = 'noreferrer';
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setActionNotice(
        missingCount > 0
          ? `Opened ${pdfTargets.length} saved PDF link${pdfTargets.length === 1 ? '' : 's'}. Skipped ${missingCount} invoice${missingCount === 1 ? '' : 's'} with no saved file.`
          : `Opened ${pdfTargets.length} saved PDF link${pdfTargets.length === 1 ? '' : 's'}.`
      );
    } catch (err: any) {
      setActionError(err?.message || 'Failed to open the selected invoice PDFs.');
    } finally {
      setActionBusy(null);
    }
  };

  const handleSendReminders = async (targets: any[]) => {
    if (!targets.length || actionBusy) return;

    clearActionFeedback();
    setActionBusy('reminder');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      let sentCount = 0;
      const skipped: string[] = [];
      const failed: string[] = [];

      for (const invoice of targets) {
        const invoiceId = getInvoiceRowId(invoice);
        const invoiceNumber = String(invoice.InvoiceNumber || invoice.InvoiceNo || invoiceId || 'Invoice').trim();

        if (!invoiceId) {
          failed.push(`${invoiceNumber}: missing invoice id`);
          continue;
        }

        const blockedReason = getReminderBlockedReason(invoice);
        if (blockedReason) {
          skipped.push(`${invoiceNumber}: ${blockedReason}`);
          continue;
        }

        try {
          await azureSqlService.sendReminderWorkflowInvoice({
            invoiceId,
            recipientEmail: String(invoice.ClientEmail || '').trim() || undefined,
          });
          sentCount += 1;
        } catch (err: any) {
          failed.push(`${invoiceNumber}: ${err?.message || 'send failed'}`);
        }
      }

      if (!sentCount && !failed.length && skipped.length) {
        setActionError(`No reminders were sent. ${skipped[0]}`);
        return;
      }

      const summaryParts: string[] = [];
      if (sentCount) summaryParts.push(`Sent ${sentCount} reminder${sentCount === 1 ? '' : 's'}`);
      if (skipped.length) summaryParts.push(`skipped ${skipped.length}`);
      if (failed.length) summaryParts.push(`failed ${failed.length}`);

      if (sentCount > 0) {
        setActionNotice(`${summaryParts.join(', ')}.`);
      }
      if (failed.length) {
        const failurePreview = failed.slice(0, 2).join(' | ');
        setActionError(sentCount > 0 ? `Some reminders failed: ${failurePreview}` : `Reminder send failed: ${failurePreview}`);
      }
      if (!sentCount && !failed.length && !skipped.length) {
        setActionError('No reminders were sent.');
      }
    } catch (err: any) {
      setActionError(err?.message || 'Failed to send invoice reminders.');
    } finally {
      setActionBusy(null);
    }
  };

  const closeReminderModal = () => {
    if (actionBusy === 'reminder') return;
    setReminderModalOpen(false);
    setReminderPreviewBusy(false);
    setReminderPreviewError(null);
    setReminderPreviewData(null);
    setReminderPreviewInvoice(null);
  };

  const openReminderModal = async (invoice: any) => {
    if (actionBusy || reminderPreviewBusy) return;

    const invoiceId = getInvoiceRowId(invoice);
    if (!invoiceId) {
      setActionError('This invoice is missing an id, so the reminder preview could not be loaded.');
      return;
    }

    const blockedReason = getReminderBlockedReason(invoice);
    if (blockedReason) {
      setActionError(blockedReason);
      return;
    }

    clearActionFeedback();
    setReminderModalOpen(true);
    setReminderPreviewBusy(true);
    setReminderPreviewError(null);
    setReminderPreviewData(null);
    setReminderPreviewInvoice(invoice);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const response = await azureSqlService.previewReminderWorkflowInvoiceEmail({
        invoiceId,
        recipientEmail: String(invoice.ClientEmail || '').trim() || undefined,
      });
      setReminderPreviewData((response?.data || response) as ReminderPreviewData);
    } catch (err: any) {
      setReminderPreviewError(err?.message || 'Failed to load the reminder email preview.');
    } finally {
      setReminderPreviewBusy(false);
    }
  };

  const handleSendReminderFromModal = async () => {
    if (!reminderPreviewData || actionBusy) return;

    const invoiceId = String(reminderPreviewData.invoiceId || '').trim();
    if (!invoiceId) {
      setReminderPreviewError('Invoice id is missing from the reminder preview.');
      return;
    }

    clearActionFeedback();
    setReminderPreviewError(null);
    setActionBusy('reminder');
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.sendReminderWorkflowInvoice({
        invoiceId,
        recipientEmail: reminderPreviewData.recipientEmail || undefined,
      });
      setActionNotice(`Sent reminder for invoice ${reminderPreviewData.invoiceNumber || invoiceId}.`);
      closeReminderModal();
    } catch (err: any) {
      setReminderPreviewError(err?.message || 'Failed to send the reminder email.');
    } finally {
      setActionBusy(null);
    }
  };

  const handleSetInvoicesPaymentState = async (targets: any[], paid: boolean) => {
    if (!targets.length || actionBusy) return;

    clearActionFeedback();
    if (!canManageInvoiceMutations) {
      setActionError('Only finance or admin users can update invoice payment state.');
      return;
    }
    setActionBusy('paid');
    try {
      const candidates = targets.filter((invoice) => getInvoicePaymentState(invoice) !== (paid ? 'Paid' : 'Not paid'));
      if (!candidates.length) {
        setActionNotice(`All selected invoices are already marked as ${paid ? 'paid' : 'unpaid'}.`);
        return;
      }

      const { azureSqlService } = await import('@/services/azureSqlService.js');
      let updatedCount = 0;
      const failed: string[] = [];
      const updatedInvoiceIds = new Set<string>();
      const paidDate = new Date().toISOString();

      for (const invoice of candidates) {
        const invoiceId = getInvoiceRowId(invoice);
        const invoiceLabel = String(invoice.InvoiceNumber || invoice.InvoiceNo || invoiceId || 'Invoice').trim();
        if (!invoiceId) {
          failed.push(`${invoiceLabel}: missing invoice id`);
          continue;
        }

        try {
          await azureSqlService.updateInvoice(invoiceId, { paid });
          updatedCount += 1;
          updatedInvoiceIds.add(invoiceId);
        } catch (err: any) {
          failed.push(`${invoiceLabel}: ${err?.message || 'update failed'}`);
        }
      }

      if (updatedCount > 0) {
        setInvoices((current) => setInvoiceRowsPaymentState(current, updatedInvoiceIds, paid, paidDate));
        setAnalyticsInvoices((current) => setInvoiceRowsPaymentState(current, updatedInvoiceIds, paid, paidDate));
        setActionNotice(`Marked ${updatedCount} invoice${updatedCount === 1 ? '' : 's'} as ${paid ? 'paid' : 'unpaid'}.`);
      }

      if (failed.length) {
        const failurePreview = failed.slice(0, 2).join(' | ');
        setActionError(updatedCount > 0 ? `Some invoice updates failed: ${failurePreview}` : `Failed to mark invoices as ${paid ? 'paid' : 'unpaid'}: ${failurePreview}`);
      }

      if (!updatedCount && !failed.length) {
        setActionError('No invoices were updated.');
      }
    } catch (err: any) {
      setActionError(err?.message || `Failed to mark invoices as ${paid ? 'paid' : 'unpaid'}.`);
    } finally {
      setActionBusy(null);
    }
  };

  console.log('🧾 Render state:', { loading, error, invoicesCount: invoices.length });

  // OneDrive-style loading state
  if (loading) {
    return (
      <div className="dgf-page">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          itemCount={0}
          gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
          icon="🧾"
          searchEnabled={false}
          sortEnabled={false}
          selectEnabled={false}
        />

        <PageSection width="wide" padding="default">
          {(aiFocus || hasActiveDateRange) ? (
            <Card padding="md">
              <div className="dgf-notice dgf-notice--info">
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI invoices for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'issue date'}</strong>.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and invoice filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
            </Card>
          ) : null}

          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⏳</div>
              <div>Loading invoices...</div>
            </div>
          </Card>
        </PageSection>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dgf-page">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          itemCount={0}
          gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
          icon="🧾"
          searchEnabled={false}
          sortEnabled={false}
          selectEnabled={false}
        />

        <PageSection width="wide" padding="default">
          {(aiFocus || hasActiveDateRange) ? (
            <Card padding="md">
              <div className="dgf-notice dgf-notice--info">
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI invoices for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'issue date'}</strong>.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and invoice filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
            </Card>
          ) : null}

          <Card padding="lg">
            <div className="dgf-state-card">
              <div className="dgf-state-card__icon">⚠️</div>
              <div className="dgf-state-card__title">Error Loading Invoices</div>
              <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
              <Button onClick={() => void loadInvoices(searchQuery.trim(), true)} variant="secondary" shape="pill">
                Try again
              </Button>
            </div>
          </Card>
        </PageSection>
      </div>
    );
  }

  return (
    <div className="dgf-page">
      <PageHeader
        title={pageTitle}
        subtitle={pageSubtitle}
        itemCount={visibleInvoices.length}
        gradientColor="linear-gradient(135deg, #173b84 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="🧾"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        density="compact"
        searchEnabled={true}
        searchQuery={searchQuery}
        searchPlaceholder="Search invoices..."
        onSearchChange={setSearchQuery}
        searchActions={(
          <Button
            type="button"
            variant="secondary"
            size="sm"
            shape="pill"
            iconBefore={<RefreshCw size={16} aria-hidden="true" />}
            loading={refreshing}
            aria-busy={refreshing}
            onClick={() => void loadInvoices(searchQuery.trim(), true)}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
        sortEnabled={true}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'date', label: 'Date' },
          { value: 'invoiceNumber', label: 'Invoice Number' },
          { value: 'client', label: 'Client' },
          { value: 'amount', label: 'Amount' },
          { value: 'status', label: 'Status' }
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        actions={[
          ...(!isNarrowScreen ? [{
            label: viewMode === 'table' ? 'Cards' : 'Table',
            onClick: () => setViewMode((current) => current === 'table' ? 'cards' : 'table'),
            variant: viewMode === 'table' ? ('primary' as const) : ('secondary' as const),
          }] : []),
          {
            label: 'Workflow',
            onClick: () => navigate('/invoices/workflow'),
            variant: 'secondary',
          },
          ...(selectMode ? [
            {
              label: actionBusy === 'export'
                ? 'Exporting...'
                : selectedInvoices.size > 1
                  ? `Export (${selectedInvoices.size})`
                  : 'Export Selected',
              onClick: () => void handleExportSelected(selectedInvoiceRecords),
              variant: 'secondary' as const,
              disabled: selectedInvoices.size === 0 || actionBusy !== null,
            },
            {
              label: actionBusy === 'pdf'
                ? 'Opening PDFs...'
                : selectedInvoices.size > 1
                  ? `Download PDFs (${selectedInvoices.size})`
                  : 'Download PDFs',
              onClick: () => void handleOpenSelectedPdfs(selectedInvoiceRecords),
              variant: 'secondary' as const,
              disabled: selectedInvoices.size === 0 || actionBusy !== null,
            },
            ...(canManageInvoiceMutations ? [
              {
                label: actionBusy === 'paid'
                  ? 'Updating...'
                  : selectedPaymentCandidates.length > 1
                    ? `Mark Paid (${selectedPaymentCandidates.length})`
                    : 'Mark Paid',
                onClick: () => void handleSetInvoicesPaymentState(selectedInvoiceRecords, true),
                variant: 'secondary' as const,
                disabled: selectedInvoices.size === 0 || selectedPaymentCandidates.length === 0 || actionBusy !== null,
              },
              {
                label: actionBusy === 'paid'
                  ? 'Updating...'
                  : selectedPaidCandidates.length > 1
                    ? `Mark Unpaid (${selectedPaidCandidates.length})`
                    : 'Mark Unpaid',
                onClick: () => void handleSetInvoicesPaymentState(selectedInvoiceRecords, false),
                variant: 'secondary' as const,
                disabled: selectedInvoices.size === 0 || selectedPaidCandidates.length === 0 || actionBusy !== null,
              },
            ] : []),
            {
              label: actionBusy === 'reminder'
                ? 'Sending...'
                : selectedReminderCandidates.length > 1
                  ? `Send Reminders (${selectedReminderCandidates.length})`
                  : 'Preview Reminder',
              onClick: () => {
                if (selectedReminderCandidates.length === 1) {
                  void openReminderModal(selectedReminderCandidates[0]);
                  return;
                }
                void handleSendReminders(selectedInvoiceRecords);
              },
              variant: 'secondary' as const,
              disabled: selectedInvoices.size === 0 || selectedReminderCandidates.length === 0 || actionBusy !== null,
            },
          ] : []),
          {
            label: selectMode ? `Selected (${selectedInvoices.size})` : 'Select',
            onClick: toggleSelectMode,
            variant: selectMode ? 'primary' : 'secondary',
            disabled: actionBusy !== null,
          },
        ]}
        selectEnabled={selectMode}
        selectedCount={selectedInvoices.size}
        totalCount={visibleInvoices.length}
        onSelectAll={toggleSelectAll}
        filters={(
          <div className="invoice-filter-controls">
            <div className="invoice-filter-controls__status">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                density="compact"
                fullWidth
                options={[
                  { value: 'all', label: 'All statuses' },
                  { value: 'Draft', label: 'Draft' },
                  { value: 'FiledPendingSend', label: 'Filed (pending send)' },
                  { value: 'Sent', label: 'Sent' },
                  { value: 'Unpaid', label: 'Unpaid' },
                  { value: 'Overdue', label: 'Overdue' },
                  { value: 'Paid', label: 'Paid' },
                  { value: 'Superseded', label: 'Superseded' },
                ]}
              />
            </div>
            <div className="invoice-filter-controls__date">
              <InvoiceDateFilterPicker
                label="Invoice date from"
                value={dateFrom}
                onChange={(value) => setDateFrom(normalizeDateOnlyInput(value))}
              />
            </div>
            <div className="invoice-filter-controls__date">
              <InvoiceDateFilterPicker
                label="Invoice date to"
                value={dateTo}
                onChange={(value) => setDateTo(normalizeDateOnlyInput(value))}
              />
            </div>
            {hasActiveDateRange ? (
              <div className="invoice-filter-controls__clear">
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  {isNarrowScreen ? 'Clear' : 'Clear Dates'}
                </Button>
              </div>
            ) : null}
          </div>
        )}
        controlBarDensity="compact"
      />

      <PageSection width="wide" padding="default">
        {(aiFocus || hasActiveDateRange) ? (
          <Card padding="md">
            <div className="dgf-notice__row">
              <div className="dgf-notice dgf-notice--info" style={{ flex: 1 }}>
                {isUsingAiPeriodRange ? (
                  <>
                    Showing Company AI invoices for <strong>{aiPeriodFilter.label}</strong> ({aiPeriodFilter.startDate} to {aiPeriodFilter.endDate}) by <strong>{aiPeriodFilter.dateBasis || 'issue date'}</strong>.
                  </>
                ) : hasActiveDateRange ? (
                  <>
                    Filtering invoices from <strong>{dateFrom || 'any start'}</strong> to <strong>{dateTo || 'any end'}</strong> by <strong>{aiPeriodFilter?.dateBasis || 'issue date'}</strong>.
                  </>
                ) : (
                  <>
                    Focused from Company AI on <strong>{aiFocus?.title}</strong>. Search and invoice filters were widened so the matched row is easier to find.
                  </>
                )}
              </div>
              {aiFollowUp?.prompt ? (
                <Button
                  variant="secondary"
                  shape="pill"
                  onClick={() => navigate(`/ai?prompt=${encodeURIComponent(aiFollowUp.prompt)}`, {
                    state: {
                      companyAiFollowUp: aiFollowUp,
                    },
                  })}
                >
                  Continue in workspace
                </Button>
              ) : null}
            </div>
          </Card>
        ) : null}

        {actionError ? (
          <Card padding="md">
            <div className="dgf-notice dgf-notice--danger">{actionError}</div>
          </Card>
        ) : null}

        {actionNotice ? (
          <Card padding="md">
            <div className="dgf-notice dgf-notice--success">{actionNotice}</div>
          </Card>
        ) : null}

        {visibleInvoices.length === 0 ? (
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '64px' }}>🧾</span>}
              title="No invoices found"
              description="Try adjusting your search or filters."
            />
          </Card>
        ) : viewMode === 'table' && !isNarrowScreen ? (
          <div className="invoice-analytics">
            <div className="invoice-summary-grid" aria-label="Invoice summary">
              <div className="invoice-summary-tile">
                <span className="invoice-summary-tile__label">Analysed invoices</span>
                <strong>{invoiceSummary.count}</strong>
              </div>
              <div className="invoice-summary-tile">
                <span className="invoice-summary-tile__label">Receivables</span>
                <strong>{formatMoney(invoiceSummary.receivablesAmount)}</strong>
              </div>
              <div className="invoice-summary-tile invoice-summary-tile--alert">
                <span className="invoice-summary-tile__label">Overdue</span>
                <strong>{formatMoney(invoiceSummary.overdueAmount)}</strong>
                <span>{invoiceSummary.overdueCount} invoice{invoiceSummary.overdueCount === 1 ? '' : 's'}</span>
              </div>
              <div className="invoice-summary-tile">
                <span className="invoice-summary-tile__label">Drafts</span>
                <strong>{invoiceSummary.draftCount}</strong>
              </div>
              <div className="invoice-summary-tile">
                <span className="invoice-summary-tile__label">Sent / unpaid</span>
                <strong>{invoiceSummary.sentCount}</strong>
              </div>
              <div className="invoice-summary-tile">
                <span className="invoice-summary-tile__label">30+ days</span>
                <strong>{invoiceSummary.thirtyPlusCount}</strong>
              </div>
            </div>

            <div className="invoice-chart-grid" aria-label="Invoice charts">
              <section className="invoice-chart-panel invoice-chart-panel--wide">
                <div className="invoice-chart-panel__header">
                  <h2>Amount by month</h2>
                  <span>{analyticsScopeLabel}</span>
                </div>
                <div className="invoice-month-chart">
                  {invoiceChartData.monthly.length > 0 ? invoiceChartData.monthly.map((item) => (
                    <div className="invoice-month-chart__bar" key={item.label}>
                      <div className="invoice-month-chart__track">
                        <span style={{ height: `${Math.max((item.amount / invoiceChartData.maxMonthlyAmount) * 100, 8)}%` }} />
                      </div>
                      <strong>{item.label}</strong>
                      <span>{formatMoney(item.amount)}</span>
                    </div>
                  )) : (
                    <div className="invoice-chart-empty">No dated invoices in this view.</div>
                  )}
                </div>
              </section>

              <section className="invoice-chart-panel">
                <div className="invoice-chart-panel__header">
                  <h2>Payment mix</h2>
                  <span>{analyticsScopeLabel}</span>
                </div>
                <div className="invoice-donut-row">
                  <div className="invoice-donut" style={{ background: invoiceChartData.statusDonut }}>
                    <span>{invoiceSummary.count}</span>
                  </div>
                  <div className="invoice-chart-legend">
                    <span><i className="invoice-chart-legend__swatch invoice-chart-legend__swatch--paid" />Paid {invoiceChartData.statusCounts.paid}</span>
                    <span><i className="invoice-chart-legend__swatch invoice-chart-legend__swatch--receivable" />Receivable {invoiceChartData.statusCounts.receivable}</span>
                    <span><i className="invoice-chart-legend__swatch invoice-chart-legend__swatch--overdue" />Overdue {invoiceChartData.statusCounts.overdue}</span>
                    <span><i className="invoice-chart-legend__swatch invoice-chart-legend__swatch--draft" />Draft {invoiceChartData.statusCounts.draft}</span>
                  </div>
                </div>
              </section>

              <section className="invoice-chart-panel">
                <div className="invoice-chart-panel__header">
                  <h2>Ageing</h2>
                  <span>Count</span>
                </div>
                <div className="invoice-horizontal-chart">
                  {invoiceChartData.ageBuckets.map((item) => (
                    <div className="invoice-horizontal-chart__row" key={item.label}>
                      <span>{item.label}</span>
                      <div>
                        <i style={{ width: `${Math.max((item.count / invoiceChartData.maxAgeCount) * 100, item.count > 0 ? 8 : 0)}%` }} />
                      </div>
                      <strong>{item.count}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="invoice-chart-panel invoice-chart-panel--wide">
                <div className="invoice-chart-panel__header">
                  <h2>Top receivables by client</h2>
                  <span>{analyticsScopeLabel}</span>
                </div>
                <div className="invoice-client-chart">
                  {invoiceChartData.topClients.length > 0 ? invoiceChartData.topClients.map((item) => (
                    <div className="invoice-client-chart__row" key={item.label}>
                      <span title={item.label}>{item.label}</span>
                      <div>
                        <i style={{ width: `${Math.max((item.amount / invoiceChartData.maxClientAmount) * 100, 6)}%` }} />
                      </div>
                      <strong>{formatMoney(item.amount)}</strong>
                    </div>
                  )) : (
                    <div className="invoice-chart-empty">No receivables in this view.</div>
                  )}
                </div>
              </section>
            </div>

            <div className="invoice-grid-toolbar">
              <div>
                <strong>Invoice table</strong>
                <span>{pagination.totalRecords} total record{pagination.totalRecords === 1 ? '' : 's'}</span>
              </div>
              <Button
                type="button"
                variant="secondary"
                shape="pill"
                onClick={handleExportGridCsv}
              >
                Export CSV
              </Button>
            </div>

            <div className="invoice-grid-shell">
              <AgGridReact
                rowData={visibleInvoices}
                columnDefs={invoiceGridColumnDefs}
                theme={invoiceGridTheme}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  floatingFilter: true,
                  resizable: true,
                }}
                getRowId={(params) => getInvoiceRowId(params.data) || String(params.data?.InvoiceNumber || '')}
                onGridReady={(params) => {
                  gridApiRef.current = params.api;
                }}
                onRowDoubleClicked={(event) => openInvoice(event.data)}
                rowHeight={46}
                headerHeight={44}
                animateRows
                suppressCellFocus={false}
              />
            </div>

            <ListPaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              currentCount={visibleInvoices.length}
              pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <div className="invoice-mobile-list" aria-label="Invoice data rows">
            {visibleInvoices.map((invoice, index) => {
              const invoiceId = getInvoiceRowId(invoice) || `invoice-${index}`;
              const effectiveStatus = getEffectiveInvoiceStatus(invoice);
              const paymentState = getInvoicePaymentState(invoice);
              const isPaid = paymentState === 'Paid';
              const openedAt = invoice.Created || invoice.IssueDate || invoice.CreatedDate;
              const ageDays = isPaid ? null : daysSince(openedAt);
              const filedPdfUrl = String(invoice.PdfFileUrl || '').trim();
              const currentRevisionNo = Number(invoice.CurrentRevisionNo || 1);
              const latestIssuedRevisionNo = invoice.LatestIssuedRevisionNo != null ? Number(invoice.LatestIssuedRevisionNo) : null;
              const latestIssuedRevisionStatus = String(invoice.LatestIssuedRevisionStatus || '').trim();
              const hasPendingRevision = Boolean(invoice.HasPendingRevision);
              const issueDateLabel = formatDateLabel(invoice.IssueDate) || 'Unknown';
              const openedLabel = formatDateLabel(openedAt) || 'Unknown';
              const overdueDays = getInvoiceOverdueDays(invoice);
              const dueTiming = getInvoiceDueTiming(invoice);
              const clientLabel = String(invoice.Client || '').trim() || 'Client pending';
              const projectLabel = buildInvoiceProjectLocation(invoice) || 'Project address pending';
              const detailMeta = [
                `Issue ${issueDateLabel}`,
                `Opened ${openedLabel}`,
                `R${currentRevisionNo}`,
                ageDays !== null ? `${ageDays} days old` : null,
              ].filter(Boolean).join('   ');
              const isAiFocusTarget = matchesCompanyAiFocusRecord(aiFocus, invoiceId);
              const canPreviewReminder = canPreviewReminderInvoice(invoice);
              const showPaymentStateAction = !selectMode && canManageInvoiceMutations;
              const showReminderAction = !selectMode && canPreviewReminder;
              const showEmailAction = !selectMode && isEmailNotificationEligibleInvoice(invoice);
              const showInvoiceFooter = Boolean(
                (hasPendingRevision && latestIssuedRevisionNo)
                || (!isNarrowScreen && showReminderAction)
                || showEmailAction
              );

              return (
                <DataRow
                  key={invoiceId}
                  id={getCompanyAiFocusDomId('invoices', invoiceId) || undefined}
                  className={isAiFocusTarget ? 'invoice-data-row dgf-row-focus-target' : 'invoice-data-row'}
                  density="compact"
                  onClick={() => {
                    if (selectMode) {
                      toggleSelection(invoiceId);
                      return;
                    }
                    navigate(`/invoices/${invoiceId}`);
                  }}
                  selectedControl={selectMode ? (
                    <input
                      type="checkbox"
                      checked={selectedInvoices.has(invoiceId)}
                      onChange={() => toggleSelection(invoiceId)}
                      onClick={(event) => event.stopPropagation()}
                      className="invoice-data-row__checkbox"
                      aria-label={`Select invoice ${invoice.InvoiceNumber || invoiceId}`}
                    />
                  ) : null}
                  title={(
                    <div className="invoice-data-row__title-line">
                      <span className="invoice-data-row__number">{invoice.InvoiceNumber || `Invoice ${index + 1}`}</span>
                      <span className="invoice-data-row__location">{projectLabel}</span>
                    </div>
                  )}
                  subtitle={clientLabel}
                  description={<span className="invoice-data-row__compact-meta">{detailMeta}</span>}
                  amount={(
                    <div className="invoice-data-row__amount-line">
                      <span>{formatMoney(invoice.TotalAmount)}</span>
                      {filedPdfUrl ? (
                        isHttpUrl(filedPdfUrl) ? (
                          <a
                            href={filedPdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="invoice-data-row__file-button"
                            onClick={(event) => event.stopPropagation()}
                          >
                            File
                          </a>
                        ) : (
                          <span className="invoice-data-row__file-button invoice-data-row__file-button--disabled">
                            File linked
                          </span>
                        )
                      ) : (
                        <span className="invoice-data-row__file-button invoice-data-row__file-button--disabled">
                          No file
                        </span>
                      )}
                    </div>
                  )}
                  status={
                    <div className="invoice-data-row__status-line">
                      <StatusBadge status={effectiveStatus} size="sm">
                        {effectiveStatus}
                      </StatusBadge>
                      <StatusBadge status={isPaid ? 'Paid' : 'Overdue'} size="sm">
                        {paymentState}
                      </StatusBadge>
                      {showPaymentStateAction ? (
                        <button
                          type="button"
                          className="invoice-data-row__text-action invoice-data-row__mark-paid-action"
                          title={`Mark invoice ${invoice.InvoiceNumber || invoiceId} as ${isPaid ? 'unpaid' : 'paid'}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!actionBusy) {
                              void handleSetInvoicesPaymentState([invoice], !isPaid);
                            }
                          }}
                          disabled={actionBusy !== null}
                        >
                          <CheckCircle2 size={13} strokeWidth={2.4} aria-hidden="true" />
                          <span className="invoice-data-row__action-label">{actionBusy === 'paid' ? '...' : isPaid ? 'Mark unpaid' : 'Mark paid'}</span>
                        </button>
                      ) : null}
                      {showReminderAction && isNarrowScreen ? (
                        <button
                          type="button"
                          className="invoice-data-row__text-action invoice-data-row__reminder-action"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void openReminderModal(invoice);
                          }}
                          disabled={actionBusy !== null || reminderPreviewBusy}
                        >
                          <Bell size={13} strokeWidth={2.4} aria-hidden="true" />
                          <span className="invoice-data-row__action-label">
                            {actionBusy === 'reminder' && reminderPreviewData?.invoiceId === invoiceId
                              ? 'Sending...'
                              : `Reminder${overdueDays ? ` ${overdueDays}d` : ''}`}
                          </span>
                        </button>
                      ) : null}
                    </div>
                  }
                  meta={
                    <span className={`invoice-data-row__due invoice-data-row__due--${dueTiming.tone}`}>
                      {dueTiming.label}
                    </span>
                  }
                  trailing={<span className="invoice-data-row__chevron" aria-hidden="true">&gt;</span>}
                  footer={showInvoiceFooter ? (
                    <div className="invoice-data-row__footer">
                      {hasPendingRevision && latestIssuedRevisionNo ? (
                        <div className="invoice-data-row__revision">
                          <strong>Revision:</strong> {`Draft revision ${currentRevisionNo} amends ${latestIssuedRevisionStatus || 'issued'} revision ${latestIssuedRevisionNo}.`}
                        </div>
                      ) : null}
                      <div className="invoice-data-row__actions">
                        {showEmailAction ? (
                          <button
                            type="button"
                            className="invoice-data-row__text-action"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setEmailTarget({
                                entityType: 'invoice',
                                entityId: Number(invoice.InvoiceId ?? invoice.Id),
                                label: String(invoice.InvoiceNumber || invoiceId),
                              });
                            }}
                            disabled={actionBusy !== null}
                          >
                            <Mail size={13} strokeWidth={2.4} aria-hidden="true" />
                            <span>Email</span>
                          </button>
                        ) : null}
                        {showReminderAction && !isNarrowScreen ? (
                          <button
                            type="button"
                            className="invoice-data-row__text-action"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void openReminderModal(invoice);
                            }}
                            disabled={actionBusy !== null || reminderPreviewBusy}
                          >
                            {actionBusy === 'reminder' && reminderPreviewData?.invoiceId === invoiceId
                              ? 'Sending...'
                              : `Reminder${overdueDays ? ` ${overdueDays}d` : ''}`}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                />
              );
            })}
            <ListPaginationControls
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              currentCount={visibleInvoices.length}
              pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(1);
              }}
            />
          </div>
        )}
      </PageSection>

      <Modal
        open={reminderModalOpen}
        onClose={closeReminderModal}
        title={`Reminder Email${reminderPreviewData?.invoiceNumber ? ` · ${reminderPreviewData.invoiceNumber}` : ''}`}
        size="lg"
        footer={(
          <>
            <Button variant="ghost" onClick={closeReminderModal} disabled={actionBusy === 'reminder'}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => { void handleSendReminderFromModal(); }}
              disabled={reminderPreviewBusy || actionBusy === 'reminder' || !reminderPreviewData?.canSend}
            >
              {actionBusy === 'reminder' ? 'Sending...' : 'Send reminder email'}
            </Button>
          </>
        )}
      >
        <div className="invoice-reminder-modal">
          {reminderPreviewInvoice ? (
            <div className="invoice-reminder-modal__summary">
              <div><strong>Client:</strong> {String(reminderPreviewInvoice.Client || '').trim() || 'Client pending'}</div>
              <div><strong>Due date:</strong> {formatDateLabel(reminderPreviewInvoice.DueDate) || 'Unknown'}</div>
              <div><strong>Overdue:</strong> {getInvoiceOverdueDays(reminderPreviewInvoice) || 0} days</div>
            </div>
          ) : null}

          {reminderPreviewBusy ? (
            <div className="invoice-reminder-modal__loading">Loading reminder preview...</div>
          ) : null}

          {reminderPreviewError ? (
            <div className="dgf-notice dgf-notice--danger">{reminderPreviewError}</div>
          ) : null}

          {!reminderPreviewBusy && reminderPreviewData ? (
            <div className="invoice-reminder-modal__content">
              {reminderPreviewData.validationMessage ? (
                <div className="dgf-notice dgf-notice--warning">{reminderPreviewData.validationMessage}</div>
              ) : null}

              <div className="invoice-reminder-modal__fields">
                <div className="invoice-reminder-modal__field">
                  <span className="invoice-reminder-modal__label">Recipient</span>
                  <div className="invoice-reminder-modal__value">{reminderPreviewData.recipientEmail || 'Not available'}</div>
                </div>
                <div className="invoice-reminder-modal__field">
                  <span className="invoice-reminder-modal__label">CC</span>
                  <div className="invoice-reminder-modal__value">
                    {Array.isArray(reminderPreviewData.ccEmails) && reminderPreviewData.ccEmails.length > 0
                      ? reminderPreviewData.ccEmails.join(', ')
                      : 'None'}
                  </div>
                </div>
                <div className="invoice-reminder-modal__field">
                  <span className="invoice-reminder-modal__label">Sender</span>
                  <div className="invoice-reminder-modal__value">{reminderPreviewData.senderEmail || 'Not available'}</div>
                </div>
                <div className="invoice-reminder-modal__field invoice-reminder-modal__field--full">
                  <span className="invoice-reminder-modal__label">Subject</span>
                  <div className="invoice-reminder-modal__value">{reminderPreviewData.subject || 'Subject pending'}</div>
                </div>
              </div>

              <div className="invoice-reminder-modal__preview">
                <div className="invoice-reminder-modal__label">Email preview</div>
                <div
                  className="invoice-reminder-modal__preview-body"
                  dangerouslySetInnerHTML={{ __html: reminderPreviewData.html || '<p>No preview available.</p>' }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      <EmailSendModal
        open={Boolean(emailTarget)}
        target={emailTarget}
        onClose={() => setEmailTarget(null)}
      />
      
    </div>
  );
};

export default InvoiceList;
